import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Stack, Tag, Question, ChartPieSlice, MagnifyingGlass, Plus, PencilSimple, Trash,
  Sparkle, ArrowsClockwise, CircleNotch, Brain,
} from "@phosphor-icons/react";

const TABS = [
  { key: "roles", label: "Rôles & Familles", icon: Stack },
  { key: "aliases", label: "Alias", icon: Tag },
  { key: "unclassified", label: "Non classés", icon: Question },
  { key: "coverage", label: "Couverture", icon: ChartPieSlice },
];

const SOURCE_BADGE = {
  seed: { label: "Référentiel", color: "#8A8A93" },
  admin: { label: "Admin", color: "#00E5FF" },
  learned: { label: "Appris (IA)", color: "#7C5CFF" },
};

const METHOD_LABEL = {
  alias: "Alias exact", fuzzy: "Similarité", fuzzy_weak: "Similarité faible",
  llm: "IA (GPT)", unknown: "Non classé", unset: "Non traité",
};

function csv(arr) { return (arr || []).join(", "); }
function parseList(str) { return (str || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean); }

export default function AdminTaxonomy() {
  const [tab, setTab] = useState("roles");
  const [families, setFamilies] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [stats, setStats] = useState(null);

  const loadBase = useCallback(async () => {
    try {
      const [f, r, s] = await Promise.all([
        api.get("/admin/taxonomy/families"),
        api.get("/admin/taxonomy/roles"),
        api.get("/admin/taxonomy/stats"),
      ]);
      setFamilies(f.data);
      setAllRoles(r.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);
  useEffect(() => { loadBase(); }, [loadBase]);

  return (
    <div className="space-y-6" data-testid="admin-taxonomy-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">Référentiel métiers</h1>
          <p className="text-sm text-muted mt-1 max-w-2xl">
            Taxonomie IT canonique : familles, rôles, alias FR/EN et normalisation des signaux.
            Corrigez un intitulé mal classé pour apprendre un alias réutilisé partout.
          </p>
        </div>
        {stats && (
          <div className="surface rounded-lg px-4 py-3 text-right">
            <div className="overline">Signaux classés</div>
            <div className="font-display text-2xl font-bold text-accent">{stats.classified_pct}%</div>
            <div className="text-xs text-muted">{stats.classified} / {stats.signals_total}</div>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-surface border border-border">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.key} value={t.key} data-testid={`tax-tab-${t.key}`}
                className="data-[state=active]:bg-surface-2 gap-1.5">
                <Icon size={15} /> {t.label}
                {t.key === "unclassified" && stats?.unclassified ? (
                  <span className="ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-accent/15 text-accent">
                    {stats.unclassified}
                  </span>
                ) : null}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {tab === "roles" && <RolesTab families={families} allRoles={allRoles} reload={loadBase} />}
      {tab === "aliases" && <AliasesTab allRoles={allRoles} />}
      {tab === "unclassified" && <UnclassifiedTab allRoles={allRoles} reload={loadBase} />}
      {tab === "coverage" && <CoverageTab stats={stats} reload={loadBase} allRoles={allRoles} />}
    </div>
  );
}

/* ------------------------------- Rôles ------------------------------- */
function RolesTab({ families, allRoles, reload }) {
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("all");
  const [roles, setRoles] = useState(allRoles);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (family !== "all") params.set("family", family);
      const { data } = await api.get(`/admin/taxonomy/roles?${params}`);
      setRoles(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, family]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const openNew = () => { setEditing({ isNew: true, family_code: families[0]?.code || "", aliases: "", skills: "", related_roles: "" }); setOpen(true); };
  const openEdit = (r) => {
    setEditing({
      ...r, aliases: (r.aliases || []).join("\n"),
      skills: csv(r.skills), related_roles: csv(r.related_roles),
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      name_fr: editing.name_fr, name_en: editing.name_en || "", family_code: editing.family_code,
      aliases: parseList(editing.aliases), skills: parseList(editing.skills),
      related_roles: parseList(editing.related_roles),
    };
    try {
      if (editing.isNew) {
        if (editing.code) payload.code = editing.code;
        await api.post("/admin/taxonomy/roles", payload);
        toast.success("Rôle créé");
      } else {
        await api.patch(`/admin/taxonomy/roles/${editing.code}`, payload);
        toast.success("Rôle mis à jour");
      }
      setOpen(false); setEditing(null); load(); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Supprimer le rôle "${r.name_fr}" et ses alias ? Les ${r.signal_count} signaux liés repasseront "non classés".`)) return;
    try { await api.delete(`/admin/taxonomy/roles/${r.code}`); toast.success("Rôle supprimé"); load(); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un rôle…"
            className="pl-9 bg-surface border-border" data-testid="tax-role-search" />
        </div>
        <Select value={family} onValueChange={setFamily}>
          <SelectTrigger className="w-56 bg-surface border-border" data-testid="tax-family-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les familles</SelectItem>
            {families.map((f) => <SelectItem key={f.code} value={f.code}>{f.name_fr} ({f.role_count})</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={openNew} data-testid="tax-new-role-btn" className="gap-1.5"><Plus size={15} /> Nouveau rôle</Button>
      </div>

      <div className="surface rounded-lg overflow-hidden">
        <div className="grid grid-cols-[1fr_180px_90px_90px_90px] gap-2 px-4 py-2.5 border-b border-border text-xs text-muted overline">
          <span>Rôle</span><span>Famille</span><span className="text-right">Alias</span>
          <span className="text-right">Signaux</span><span className="text-right">Actions</span>
        </div>
        {loading ? <div className="p-6 text-muted text-sm animate-pulse">Chargement…</div>
          : roles.length === 0 ? <div className="p-6 text-muted text-sm">Aucun rôle.</div>
            : roles.map((r) => (
              <div key={r.code} data-testid={`tax-role-row-${r.code}`}
                className="grid grid-cols-[1fr_180px_90px_90px_90px] gap-2 px-4 py-3 border-b border-border/50 items-center hover:bg-surface-2/40">
                <div>
                  <div className="text-sm text-fg">{r.name_fr}</div>
                  <div className="text-[11px] text-muted font-mono">{r.code}{r.name_en ? ` · ${r.name_en}` : ""}</div>
                </div>
                <span className="text-xs text-muted">{r.family_name}</span>
                <span className="text-right text-sm font-mono text-fg/80">{r.alias_count}</span>
                <span className="text-right text-sm font-mono text-accent">{r.signal_count}</span>
                <div className="flex justify-end gap-1">
                  <button onClick={() => openEdit(r)} data-testid={`tax-role-edit-${r.code}`}
                    className="p-1.5 rounded hover:bg-surface-2 text-muted hover:text-fg"><PencilSimple size={15} /></button>
                  <button onClick={() => del(r)} data-testid={`tax-role-delete-${r.code}`}
                    className="p-1.5 rounded hover:bg-red-500/10 text-muted hover:text-red-400"><Trash size={15} /></button>
                </div>
              </div>
            ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-border max-w-lg max-h-[88vh] overflow-y-auto" data-testid="tax-role-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing?.isNew ? "Nouveau rôle" : "Modifier le rôle"}</DialogTitle>
            <DialogDescription className="text-muted">Rôle canonique du référentiel métiers</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Field label="Nom (FR)">
                <Input value={editing.name_fr || ""} onChange={(e) => setEditing({ ...editing, name_fr: e.target.value })}
                  className="bg-surface-2 border-border" data-testid="tax-role-name-fr" />
              </Field>
              <Field label="Nom (EN)">
                <Input value={editing.name_en || ""} onChange={(e) => setEditing({ ...editing, name_en: e.target.value })}
                  className="bg-surface-2 border-border" data-testid="tax-role-name-en" />
              </Field>
              {editing.isNew && (
                <Field label="Code (optionnel, auto-généré)">
                  <Input value={editing.code || ""} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                    placeholder="EX: BACK_JAVA" className="bg-surface-2 border-border font-mono" data-testid="tax-role-code" />
                </Field>
              )}
              <Field label="Famille">
                <Select value={editing.family_code} onValueChange={(v) => setEditing({ ...editing, family_code: v })}>
                  <SelectTrigger className="bg-surface-2 border-border" data-testid="tax-role-family"><SelectValue /></SelectTrigger>
                  <SelectContent>{families.map((f) => <SelectItem key={f.code} value={f.code}>{f.name_fr}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Alias (un par ligne — intitulés FR/EN reconnus)">
                <Textarea value={editing.aliases} onChange={(e) => setEditing({ ...editing, aliases: e.target.value })}
                  rows={5} className="bg-surface-2 border-border font-mono text-xs" data-testid="tax-role-aliases"
                  placeholder={"dev java\ningénieur java\njava developer"} />
              </Field>
              <Field label="Compétences (séparées par des virgules)">
                <Input value={editing.skills} onChange={(e) => setEditing({ ...editing, skills: e.target.value })}
                  className="bg-surface-2 border-border" data-testid="tax-role-skills" placeholder="Java, Spring, REST" />
              </Field>
              <Field label="Rôles proches (codes séparés par des virgules)">
                <Input value={editing.related_roles} onChange={(e) => setEditing({ ...editing, related_roles: e.target.value })}
                  className="bg-surface-2 border-border font-mono text-xs" data-testid="tax-role-related" placeholder="BACK_SPRING, ARCH_JAVA" />
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-border">Annuler</Button>
            <Button onClick={save} disabled={!editing?.name_fr || !editing?.family_code} data-testid="tax-role-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------- Alias ------------------------------- */
function AliasesTab({ allRoles }) {
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newAlias, setNewAlias] = useState("");
  const [newRole, setNewRole] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (source !== "all") params.set("source", source);
      const { data } = await api.get(`/admin/taxonomy/aliases?${params}`);
      setItems(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, source]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const add = async () => {
    if (!newAlias.trim() || !newRole) return;
    try {
      await api.post("/admin/taxonomy/aliases", { alias: newAlias.trim(), role_code: newRole });
      toast.success("Alias ajouté"); setNewAlias(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (a) => {
    try { await api.delete(`/admin/taxonomy/aliases/${a.id}`); toast.success("Alias supprimé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="surface rounded-lg p-4 flex items-end gap-3 flex-wrap">
        <Field label="Nouvel alias (intitulé reconnu)" className="flex-1 min-w-[200px]">
          <Input value={newAlias} onChange={(e) => setNewAlias(e.target.value)} placeholder="ex: ingénieur cloud aws"
            className="bg-surface-2 border-border" data-testid="tax-alias-input" />
        </Field>
        <Field label="Rôle cible" className="min-w-[220px]">
          <RoleSelect roles={allRoles} value={newRole} onChange={setNewRole} testid="tax-alias-role" />
        </Field>
        <Button onClick={add} disabled={!newAlias.trim() || !newRole} data-testid="tax-alias-add-btn" className="gap-1.5"><Plus size={15} /> Ajouter</Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un alias…"
            className="pl-9 bg-surface border-border" data-testid="tax-alias-search" />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-44 bg-surface border-border" data-testid="tax-alias-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes sources</SelectItem>
            <SelectItem value="seed">Référentiel</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="learned">Appris (IA)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="surface rounded-lg overflow-hidden">
        {loading ? <div className="p-6 text-muted text-sm animate-pulse">Chargement…</div>
          : items.length === 0 ? <div className="p-6 text-muted text-sm">Aucun alias.</div>
            : items.map((a) => {
              const b = SOURCE_BADGE[a.source] || SOURCE_BADGE.admin;
              return (
                <div key={a.id} data-testid={`tax-alias-row-${a.id}`}
                  className="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 hover:bg-surface-2/40">
                  <span className="text-sm text-fg flex-1 truncate">{a.alias}</span>
                  <span className="text-xs text-muted truncate w-48">{a.role_name}</span>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: b.color + "1a", color: b.color }}>{b.label}</span>
                  {a.source !== "seed" && (
                    <button onClick={() => del(a)} data-testid={`tax-alias-delete-${a.id}`}
                      className="p-1.5 rounded hover:bg-red-500/10 text-muted hover:text-red-400"><Trash size={14} /></button>
                  )}
                </div>
              );
            })}
      </div>
    </div>
  );
}

/* --------------------------- Non classés --------------------------- */
function UnclassifiedTab({ allRoles, reload }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(null);
  const [llmBusy, setLlmBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/taxonomy/unclassified?limit=80"); setItems(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const map = async (title, i) => {
    const role = picks[i];
    if (!role) { toast.error("Choisissez un rôle"); return; }
    setBusy(i);
    try {
      await api.post("/admin/taxonomy/map-title", { title, role_code: role });
      toast.success("Intitulé mappé et signaux reclassés");
      load(); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const classifyLlm = async () => {
    setLlmBusy(true);
    try {
      await api.post("/admin/taxonomy/reclassify", { allow_llm: true, limit: 200, only_missing: true });
      toast.success("Classification IA lancée (jusqu'à 200 intitulés). Actualisez dans ~1 min.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLlmBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="surface rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted max-w-xl">
          Intitulés bruts que la normalisation locale n'a pas résolus. Mappez-les vers un rôle
          (l'alias est appris) ou lancez une passe IA pour classer automatiquement les inconnus.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="border-border gap-1.5"><ArrowsClockwise size={15} /> Actualiser</Button>
          <Button onClick={classifyLlm} disabled={llmBusy} data-testid="tax-unclassified-reclassify-llm" className="gap-1.5">
            {llmBusy ? <CircleNotch size={15} className="animate-spin" /> : <Brain size={15} />} Classer les inconnus via IA
          </Button>
        </div>
      </div>

      <div className="surface rounded-lg overflow-hidden">
        {loading ? <div className="p-6 text-muted text-sm animate-pulse">Chargement…</div>
          : items.length === 0 ? <div className="p-6 text-muted text-sm">🎉 Aucun intitulé non classé.</div>
            : items.map((it, i) => (
              <div key={i} data-testid={`tax-unclassified-row-${i}`}
                className="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 hover:bg-surface-2/40">
                <span className="text-[11px] font-mono w-10 text-accent">×{it.count}</span>
                <span className="text-sm text-fg flex-1 truncate" title={it.title}>{it.title}</span>
                <div className="w-52"><RoleSelect roles={allRoles} value={picks[i] || ""} onChange={(v) => setPicks({ ...picks, [i]: v })} testid={`tax-map-role-${i}`} /></div>
                <Button size="sm" onClick={() => map(it.title, i)} disabled={busy === i || !picks[i]} data-testid={`tax-map-btn-${i}`}>
                  {busy === i ? <CircleNotch size={14} className="animate-spin" /> : "Mapper"}
                </Button>
              </div>
            ))}
      </div>
    </div>
  );
}

/* --------------------------- Couverture --------------------------- */
function CoverageTab({ stats, reload, allRoles }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");
  const [pvResult, setPvResult] = useState(null);
  const [pvBusy, setPvBusy] = useState(false);

  const reclassifyLocal = async () => {
    setBusy(true);
    try {
      await api.post("/admin/taxonomy/reclassify", { allow_llm: false, only_missing: false });
      toast.success("Reclassification locale lancée. Actualisez dans quelques secondes.");
      setTimeout(reload, 4000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runPreview = async () => {
    if (!preview.trim()) return;
    setPvBusy(true); setPvResult(null);
    try {
      const { data } = await api.post("/admin/taxonomy/classify-preview", { title: preview.trim(), allow_llm: true });
      setPvResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPvBusy(false); }
  };

  if (!stats) return <div className="text-muted text-sm animate-pulse">Chargement…</div>;
  const maxFam = Math.max(1, ...(stats.top_families || []).map((f) => f.count));
  const roleName = pvResult?.canonical_role_code ? (allRoles.find((r) => r.code === pvResult.canonical_role_code)?.name_fr || pvResult.canonical_role_name) : null;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Signaux classés" value={`${stats.classified_pct}%`} sub={`${stats.classified} / ${stats.signals_total}`} color="#00E5FF" testid="tax-stat-classified" />
        <Stat label="Non classés" value={stats.unclassified} sub="à mapper / IA" color="#F59E0B" testid="tax-stat-unknown" />
        <Stat label="Rôles · Familles" value={`${stats.roles_count} · ${stats.families_count}`} sub="dans le référentiel" color="#7C5CFF" />
        <Stat label="Alias" value={stats.aliases_seed + stats.aliases_admin + stats.aliases_learned}
          sub={`${stats.aliases_seed} ref · ${stats.aliases_admin} admin · ${stats.aliases_learned} IA`} color="#22C55E" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="surface rounded-lg p-5">
          <div className="overline mb-4">Top familles (signaux)</div>
          <div className="space-y-2.5">
            {(stats.top_families || []).map((f) => (
              <div key={f.family}>
                <div className="flex justify-between text-xs mb-1"><span className="text-fg/85">{f.family}</span><span className="font-mono text-muted">{f.count}</span></div>
                <div className="h-2 rounded-full bg-surface-2 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(f.count / maxFam) * 100}%`, background: "#00E5FF" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="surface rounded-lg p-5">
          <div className="overline mb-4">Méthode de classification</div>
          <div className="space-y-2.5">
            {Object.entries(stats.by_method || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <div key={k} className="flex justify-between items-center text-sm">
                <span className="text-fg/85">{METHOD_LABEL[k] || k}</span>
                <span className="font-mono text-muted">{v}</span>
              </div>
            ))}
          </div>
          <Button variant="outline" onClick={reclassifyLocal} disabled={busy} data-testid="tax-reclassify-local"
            className="w-full mt-4 border-border gap-1.5">
            {busy ? <CircleNotch size={15} className="animate-spin" /> : <ArrowsClockwise size={15} />} Reclassifier tout (local)
          </Button>
        </div>
      </div>

      <div className="surface rounded-lg p-5">
        <div className="overline mb-3 flex items-center gap-1.5"><Sparkle size={14} className="text-accent" /> Tester un intitulé</div>
        <div className="flex gap-2">
          <Input value={preview} onChange={(e) => setPreview(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runPreview()}
            placeholder="ex: Ingénieur DevOps AWS (H/F)" className="bg-surface-2 border-border" data-testid="tax-preview-input" />
          <Button onClick={runPreview} disabled={pvBusy} data-testid="tax-preview-btn">
            {pvBusy ? <CircleNotch size={15} className="animate-spin" /> : "Analyser"}
          </Button>
        </div>
        {pvResult && (
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm" data-testid="tax-preview-result">
            {pvResult.canonical_role_code ? (
              <>
                <span className="px-2.5 py-1 rounded bg-accent/15 text-accent font-medium">{roleName}</span>
                <span className="text-muted">Famille : <span className="text-fg/85">{pvResult.canonical_family_name}</span></span>
                <span className="text-muted">Méthode : <span className="text-fg/85">{METHOD_LABEL[pvResult.role_method] || pvResult.role_method}</span></span>
                <span className="text-muted">Confiance : <span className="text-fg/85">{pvResult.role_confidence}%</span></span>
              </>
            ) : <span className="text-amber-400">Non classé — aucun rôle canonique correspondant.</span>}
            <span className="text-[11px] text-muted font-mono w-full">nettoyé : "{pvResult.cleaned}"</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- Shared ------------------------------- */
function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs text-muted mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function Stat({ label, value, sub, color = "#00E5FF", testid }) {
  return (
    <div className="surface rounded-lg p-5" data-testid={testid}>
      <div className="overline mb-2" style={{ color }}>{label}</div>
      <div className="font-display text-3xl font-bold text-fg">{value}</div>
      {sub && <div className="text-xs text-muted mt-1">{sub}</div>}
    </div>
  );
}

function RoleSelect({ roles, value, onChange, testid }) {
  const byFam = {};
  roles.forEach((r) => { (byFam[r.family_name || "Autres"] = byFam[r.family_name || "Autres"] || []).push(r); });
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="bg-surface-2 border-border h-9 text-xs" data-testid={testid}><SelectValue placeholder="Choisir un rôle…" /></SelectTrigger>
      <SelectContent className="max-h-72">
        {Object.entries(byFam).sort().map(([fam, rs]) => (
          <div key={fam}>
            <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted">{fam}</div>
            {rs.map((r) => <SelectItem key={r.code} value={r.code} className="text-xs">{r.name_fr}</SelectItem>)}
          </div>
        ))}
      </SelectContent>
    </Select>
  );
}
